import React, {
  useEffect, useState, useCallback, useMemo,
  useRef,
} from 'react';
import styled from 'styled-components';
import {
  getErrors,
  TextInput,
  password as passwordRules,
  useStickySafeNavigate,
  Flex,
  PrimaryButton,
  GeneralLoginPage,
  LoginFormGrid,
  LoginFormTitle,
  useIsCapsLockStatus,
  TextFieldProps,
  ValidationFunction,
  useAuthResetPasswordApi,
  LoginFormTitleIcon,
  flattenErrors,
  CloseIcon,
  useStyledTheme,
  CheckBolderIcon,
  TextFieldInput,
  useIsXOverflow,
  useQuery,
  Tooltip,
  FlexColumn,
  useAsyncFunctionProgress,
  requiredWithMessage,
  useToastNotifications,
  ToastStatus,
  LoginLinkStyled,
} from '@equifinance/shared';
import { useSetupPasswordApi } from '../api/useSetupPassword';

type PasswordsFieldName = 'password' | 'repeatPassword'

const requiredErrMsg = 'This field is required.';
const requiredValidation = requiredWithMessage(requiredErrMsg);

const setupPasswordRule: ValidationFunction[] = [requiredValidation, ...passwordRules];

const MainPage = styled.div`
  ${LoginFormGrid} {
    display: flex;
  };
`;

const SubTitle = styled.p`${({ theme }) => `
  font-size: ${theme.fontSize[4]};
  line-height: ${theme.padding[7]};
  font-weight: ${theme.fontWeight.bold};
  color: ${theme.colours.primary[1]};
  text-align: left;
  margin: 0;
`}`;

const UserInfoWrapper = styled(Flex)`${({ theme }) => `
  gap: ${theme.padding[1]};
  width: 100%;
  min-width: 0;
`}`;

const UserInfoLabel = styled.div`${({ theme }) => `
  font-family: 'Open Sans';
  font-weight: ${theme.fontWeight.normal};
  font-size: ${theme.fontSize[2]};
  line-height: ${theme.fontSize[6]};
  color: ${theme.colours.neutral[2]};
  flex-shrink: 0;
`}`;

const UserInfoValue = styled(UserInfoLabel)`${({ theme }) => `
  font-weight: ${theme.fontWeight.bold};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
`}`;

const BreakLine = styled.div`${({ theme }) => `
  width: 100%;
  border-bottom: ${theme.borderWidth[2]} dashed ${theme.colours.primary[4]};
`}`;

const BackToLoginLink = styled(Flex)`${({ theme }) => `
  gap: ${theme.padding[1]};
  font-size: ${theme.fontSize[3]};
  color: ${theme.colours.neutral[2]};
  align-items: center;
`}`;

export const PasswordRuleWrapper = styled(Flex)`${({ theme }) => `
  flex-direction: column;
  display: grid;
  grid-gap: ${theme.padding[2]} 0;
  margin-top: ${theme.padding[2]};
`}`;

export const PasswordRuleItem = styled(Flex)`${({ theme }) => `
  display: grid;
  align-items: center;
  grid-template-columns: ${theme.fontSize[3]} 1fr;
  grid-gap: 0 ${theme.padding[1]};
`}`;

export const PasswordRuleIcon = styled(Flex)`${() => `
  align-items: center;
  justify-content: center;
`}`;

export const PasswordRuleLabel = styled.div`${({ theme }) => `
  font-family: 'Open Sans';
  font-weight: ${theme.fontWeight.normal};
  font-size: ${theme.fontSize[1]};
  line-height: ${theme.fontSize[3]};
  color: ${theme.colours.neutral[2]};
`}`;

export const ShowHideWrapper = styled(Flex)`${({ theme }) => `
  cursor: default;
  align-items: center;
  padding-right: ${theme.borderWidth[2]};
`}`;

export const DotIcon = styled.span`${({ theme }) => `
  background-color: ${theme.colours.neutral[2]};
  width: ${theme.padding[1]};
  height: ${theme.padding[1]};
  border-radius: 50%;
`}`;

const ShowHideIcon = styled.i`${({ theme }) => `
  font-size: ${theme.fontSize[3]};
  margin-right: ${theme.padding[1]};
  color: ${theme.colours.neutral[3]};
`}`;

const ShowHideLabel = styled.span`${({ theme }) => `
  font-family: 'Open Sans';
  font-size: ${theme.fontSize[1]};
  line-height: ${theme.fontSize[3]};
  color: ${theme.colours.neutral[3]};
`}`;

export const RowWrapper = styled(Flex)`
  flex-direction: column;
`;

const InfoText = styled.p`${({ theme }) => `
  font-size: ${theme.fontSize[4]};
  line-height: ${theme.padding[7]};
  color: ${theme.colours.neutral[2]};
`}`;

const InfoTextBold = styled(InfoText)`${({ theme }) => `
  font-weight: ${theme.fontWeight.bold};
`}`;

const SuccessIcon = styled.i.attrs(() => ({
  className: 'mi-check',
}))`${({ theme }) => `
  font-size: ${theme.taskEvidenceActionsWidth};
  color: ${theme.colours.primary[1]};
`}`;

const WarningIcon = styled.i.attrs(() => ({
  className: 'mi-warning',
}))`${({ theme }) => `
  font-size: ${theme.sidebarCollapsedWidth};
  color: ${theme.colours.warning[1]};
`}`;

const StyledPasswordInput = styled.div<{ state?: 'success' | 'error' | null }>`${({ theme, state }) => `
  ${TextFieldInput} {
    &:focus {
      box-shadow: 0px 0px 0px 5px ${theme.colours.primary[11]};
      outline: none;
      ${!state && `{
        border-color: ${theme.colours.primary[5]};
      }`};
    }
    border: 1px solid ${theme.colours.neutral[6]};
    ${state === 'success' && `{
      border-color: ${theme.colours.success[1]};
    }`};
    ${state === 'error' && `{
      border-color: ${theme.colours.danger[1]};
      &:focus {  
        box-shadow: 0px 0px 0px 5px ${theme.colours.danger[2]};
      }
    }`};
  };
`}`;

const tokenInvalidMessage = 'Your link has expired. Please request a new one to continue';

export function PasswordSuffix({ isShow }: { isShow: boolean }) {
  return (
    <ShowHideWrapper>
      <ShowHideIcon className={isShow ? 'mi-eye' : 'mi-eye-off'} />
      <ShowHideLabel>{isShow ? 'Hide' : 'Show'}</ShowHideLabel>
    </ShowHideWrapper>
  );
}

function PaswordInput({ errors, ...props }: TextFieldProps) {
  const [isShowPass, setIsShowPass] = useState(false);
  const [isFocus, setIsFocus] = useState(false);
  const [isBlured, setIsBlured] = useState(false);
  const capsLockOn = useIsCapsLockStatus();

  const errorList = useMemo(() => {
    const list = isBlured ? errors || [] : [];
    if (isFocus && capsLockOn) {
      if (isBlured) return ['Caps lock is turned on', ...list];
      return ['Caps lock is turned on'];
    }
    return list;
  }, [capsLockOn, isFocus, errors]);

  const inputState = useMemo(() => {
    if (!isBlured) return null;
    if (errors?.length) return 'error';
    return 'success';
  }, [errors, isBlured]);

  return (
    <StyledPasswordInput state={inputState}>
      <TextInput
        {...props}
        type={isShowPass ? 'text' : 'password'}
        suffix={<PasswordSuffix isShow={isShowPass} />}
        onClickSuffix={() => setIsShowPass(!isShowPass)}
        onFocus={() => setIsFocus(true)}
        onBlur={() => {
          setIsBlured(true);
          setIsFocus(false);
        }}
        errors={errorList}
        touchOverride
        required
      />
    </StyledPasswordInput>
  );
}

function Landing({ isSetupPassword = false, onConfirm }: { isSetupPassword?: boolean, onConfirm: () => void }) {
  const title = isSetupPassword ? 'Welcome\nto Equifinance' : 'Forgot your password?';
  const subTitle = `You are about to ${
    isSetupPassword
      ? 'set the password'
      : 'reset the password'
  } for your account, do you wish to continue?`;
  const note = `If you did not expect to receive this password ${
    isSetupPassword ? 'setup' : 'reset'
  } request, please contact your Equifinance administrator immediately.`;

  return (
    <GeneralLoginPage>
      <LoginFormTitle>{title}</LoginFormTitle>
      <SubTitle>{subTitle}</SubTitle>
      <UserInfoLabel>{note}</UserInfoLabel>
      <PrimaryButton
        id="confirmed-btn"
        onClick={onConfirm}
      >
        Confirm
      </PrimaryButton>
      <BackToLoginLink>
        Return to
        <LoginLinkStyled to="/login">Login</LoginLinkStyled>
      </BackToLoginLink>
    </GeneralLoginPage>
  );
}

function Success({ isSetupPassword = false }: { isSetupPassword?: boolean }) {
  const navigate = useStickySafeNavigate();
  const title = isSetupPassword ? 'Password created successfully' : 'Your password has been successfully set';

  return (
    <GeneralLoginPage>
      <LoginFormTitleIcon>
        <SuccessIcon />
      </LoginFormTitleIcon>
      <LoginFormTitle>{title}</LoginFormTitle>
      {!isSetupPassword
        && (
          <InfoText>
            You will receive an email confirming your password reset.
          </InfoText>
        )}
      <PrimaryButton
        id="confirmed-btn"
        onClick={() => navigate('/login')}
      >
        Continue to Login
      </PrimaryButton>
    </GeneralLoginPage>
  );
}

function Expired() {
  return (
    <GeneralLoginPage>
      <LoginFormTitleIcon>
        <WarningIcon />
      </LoginFormTitleIcon>
      <LoginFormTitle>The link has expired or has already been used.</LoginFormTitle>
      <InfoTextBold>
        Please contact Equifinance for more information.
      </InfoTextBold>
    </GeneralLoginPage>
  );
}

function UserInfo({ label, value }: { label: string; value: string }) {
  const nameRef = useRef<HTMLDivElement>(null);

  const isTruncated = useIsXOverflow(nameRef, [value]);

  return (
    <UserInfoWrapper>
      <UserInfoLabel>{label}</UserInfoLabel>
      <UserInfoValue ref={nameRef} id={`user-info-${label}`}>
        {value}
      </UserInfoValue>
      {isTruncated && (
        <Tooltip
          id={`user-info-${label}`}
          html={value}
          place="top"
        />
      )}
    </UserInfoWrapper>
  );
}

export default function ResetPassword({ isSetupPassword = false }: { isSetupPassword?: boolean }) {
  const { token, ref, name } = useQuery();
  const [showLanding, setShowLanding] = useState(true);
  const [inputs, setInputs] = useState({ password: '', repeatPassword: '' });
  const [result, setResult] = useState<'error' | 'success'>();
  const { post: resetPassword } = useAuthResetPasswordApi();
  const { post: setupPassword } = useSetupPasswordApi();
  const theme = useStyledTheme();

  const { addToastNotification } = useToastNotifications();

  const expirationTime = useMemo(() => {
    const expirationTimeToken = token?.split('.')?.[0] ?? '';
    try {
      return +window.atob(expirationTimeToken) || 0;
    } catch {
      return 0;
    }
  }, [token]);

  const email = useMemo(() => {
    try {
      return window.atob(ref ?? '');
    } catch {
      return '';
    }
  }, [ref]);

  const displayName = useMemo(() => {
    try {
      return window.atob(name ?? '');
    } catch {
      return '';
    }
  }, [name]);

  const errors: Record<PasswordsFieldName, string[]> = useMemo(() => {
    const passwordErrors = getErrors(inputs.password, setupPasswordRule);

    if (!inputs.repeatPassword) {
      return {
        password: passwordErrors,
        repeatPassword: [requiredErrMsg],
      };
    }

    const repeatPasswordErrors = [];
    if (inputs.password !== inputs.repeatPassword) {
      repeatPasswordErrors.push('Password does not match.');
    }

    return {
      password: passwordErrors,
      repeatPassword: repeatPasswordErrors,
    };
  }, [inputs]);

  const passwordErrorMessage = useMemo(() => {
    if (errors.password.length === 0) {
      return [];
    }
    if (errors.password.includes(requiredErrMsg)) {
      return [requiredErrMsg];
    }
    return ['Password does not meet requirements.'];
  }, [errors.password]);

  const onChange = (field: PasswordsFieldName, value: string) => {
    setInputs((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const canConfirm = !errors.password.length && !errors.repeatPassword.length;

  const handleResetPassword = async () => {
    if (canConfirm) {
      const result = await resetPassword({
        token: token!,
        emailAddress: email,
        password: inputs.password,
      });

      if (result.errors && flattenErrors(result.errors).length) {
        const isTokenInvalid = flattenErrors(result.errors).find((e) => e.errorMessage === tokenInvalidMessage);
        if (isTokenInvalid) {
          setResult('error');
        }
      } else {
        setResult('success');
      }
    }
  };

  const hanleSetupPassword = async () => {
    try {
      if (canConfirm) {
        await setupPassword({
          token: token!,
          emailAddress: email,
          password: inputs.password,
        });

        setResult('success');
      }
    } catch (e) {
      addToastNotification({
        text: 'Something went wrong. If the issue persists, please contact your admin.',
        type: ToastStatus.ERROR,
        showCloseIcon: true,
      });
    }
  };

  const onConfirm = async () => {
    try {
      await (isSetupPassword ? hanleSetupPassword() : handleResetPassword());
    } catch {
      setResult('error');
    }
  };

  const { inProgress: confirmInProgress, execute: executeConfirm } = useAsyncFunctionProgress(onConfirm);

  const ValidateIcon = useCallback((rule: string) => {
    if (!errors.password || errors?.password?.includes(requiredErrMsg)) {
      return (<DotIcon />);
    }
    if (errors?.password?.includes(rule)) {
      return (<CloseIcon fill={theme.colours.danger[1]} width={13} />);
    }
    return (<CheckBolderIcon fill={theme.colours.success[1]} width={12} height={10} />);
  }, [errors]);

  useEffect(() => {
    if (!token || !email) {
      setResult('error');
    }
  }, [token, email]);

  useEffect(() => {
    if (expirationTime > 0 && expirationTime < Date.now()) {
      setResult('error');
    }
  }, [expirationTime]);

  if (showLanding) {
    return <Landing isSetupPassword={isSetupPassword} onConfirm={() => setShowLanding(false)} />;
  }

  if (result === 'error') {
    return (
      <Expired />
    );
  }

  if (result === 'success') {
    return (
      <Success isSetupPassword={isSetupPassword} />
    );
  }

  const formTitle = isSetupPassword ? 'Welcome\nto Equifinance' : 'Reset your password';

  return (
    <MainPage>
      <GeneralLoginPage>
        <LoginFormGrid>
          <LoginFormTitle>{formTitle}</LoginFormTitle>
          {isSetupPassword && <SubTitle>Please set up your password</SubTitle>}
          <FlexColumn>
            <UserInfo label="Name:" value={displayName} />
            <UserInfo label="Email:" value={email} />
          </FlexColumn>
          <BreakLine />
          <RowWrapper>
            <PaswordInput
              id="password"
              label="New password"
              onChange={(value: string) => onChange('password', value)}
              value={inputs.password}
              errors={passwordErrorMessage}
            />
            <PasswordRuleWrapper>
              <PasswordRuleLabel>
                Password must:
              </PasswordRuleLabel>
              {passwordRules.map((rule) => (
                <PasswordRuleItem key={`${rule.error}-text`}>
                  <PasswordRuleIcon>{ValidateIcon(rule.error)}</PasswordRuleIcon>
                  <PasswordRuleLabel>{rule.error}</PasswordRuleLabel>
                </PasswordRuleItem>
              ))}
            </PasswordRuleWrapper>
          </RowWrapper>
          <RowWrapper>
            <PaswordInput
              id="repeatPassword"
              label="Re-enter your new password"
              onChange={(value: string) => onChange('repeatPassword', value)}
              value={inputs.repeatPassword}
              errors={errors.repeatPassword}
            />
          </RowWrapper>
          <PrimaryButton
            disabled={!canConfirm || confirmInProgress}
            onClick={executeConfirm}
          >
            Confirm
          </PrimaryButton>
        </LoginFormGrid>
      </GeneralLoginPage>
    </MainPage>
  );
}
